<template>
  <div class="max-w-4xl mx-auto p-6">
    <v-dialog
      :value="showSaleQuoteModal"
      max-width="500"
      :persistent="loadingSaleQuote"
      @input="onSaleQuoteModalInput"
    >
      <v-card v-if="saleQuote && currentAsset">
        <v-card-title class="d-flex align-center justify-space-between">
          Confirm Sale
          <v-btn
            icon
            small
            :disabled="loadingSaleQuote"
            aria-label="Close"
            @click="onSaleQuoteModalInput(false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div class="flex flex-row items-center justify-between mt-8">
            <div class="flex flex-col items-center justify-center">
              <div class="flex flex-row items-center gap-2 text-center mb-2">
                <img
                  class="w-6 h-6 rounded-full overflow-hidden"
                  :src="currentAsset.logo"
                  :alt="`${currentAsset.symbol} logo`"
                />
                <span>{{ currentAsset.symbol }}</span>
              </div>
              <span>{{ amount }} {{ currentAsset.symbol }}</span>
            </div>
            <div class="flex flex-col items-center justify-center">
              <v-icon>mdi-arrow-right</v-icon>
            </div>
            <div class="flex flex-col items-center justify-center">
              <div class="flex flex-row items-center gap-2 text-center mb-2">
                <span>NGN</span>
              </div>
              <span>{{ saleQuote.to_amount }} NGN</span>
            </div>
          </div>
          <div class="mt-4">
            <div class="grid grid-cols-2 gap-4">
              <div>Rate</div>
              <div class="text-left">
                {{ saleQuote.rate }} {{ saleQuote.from_currency }} per
                {{ saleQuote.to_currency }}
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>From</div>
              <div class="text-left">
                {{ saleQuote.from_amount }} {{ saleQuote.from_currency }}
              </div>
            </div>
            <div class="grid grid-cols-2 gap-4">
              <div>To</div>
              <div class="text-left">
                {{ saleQuote.to_amount }} {{ saleQuote.to_currency }}
              </div>
            </div>
          </div>

          <div
            class="mt-8 text-center"
            :class="{ 'error--text': quoteIsExpired }"
          >
            <span v-if="quoteIsExpired">
              This quote has expired. Refresh to get a new rate.
            </span>
            <span v-else>Quote expires in {{ saleQuoteExpiresIn }} seconds</span>
          </div>

          <div class="mt-8">
            <v-btn
              color="primary"
              block
              @click="completeSaleQuote"
              :loading="loadingSaleQuote"
              :disabled="loadingSaleQuote || quoteIsExpired"
            >
              Complete Sale
            </v-btn>
            <v-btn
              color="primary"
              text
              block
              @click="refreshSaleQuote"
              :loading="loadingSaleQuote"
              :disabled="loadingSaleQuote"
            >
              Refresh Quote
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center">
        <v-btn icon small class="mr-3" @click="goBack" aria-label="Go back">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <span class="text-gray-800 font-medium">Back</span>
      </div>

      <h1 class="text-3xl font-bold text-gray-800 mb-2 mt-4">
        Convert {{ selectedAssetSymbol || 'Crypto' }} to NGN
      </h1>
      <p class="text-gray-600">
        Sell your crypto directly into your NGN wallet balance.
      </p>
    </div>

    <div
      v-if="initialLoading"
      class="rounded-xl border border-gray-200 bg-white p-8 text-center"
    >
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>

    <div v-else-if="walletAddressData && currentAsset">
      <div class="flex flex-row justify-between items-center">
        <span>From:</span>
        <span>
          Balance: {{ formattedWalletBalance }} {{ selectedAssetSymbol }}
        </span>
      </div>
      <div class="flex flex-row items-center gap-2 justify-between mt-4">
        <div
          class="inline-flex flex-row items-center gap-2 bg-white rounded-lg p-2 border border-gray-200"
        >
          <div class="w-6 h-6 rounded-full overflow-hidden">
            <img
              class="w-full h-full object-cover"
              :src="currentAsset.logo"
              :alt="`${currentAsset.symbol} logo`"
            />
          </div>
          <span>{{ currentAsset.symbol }}</span>
        </div>
        <div class="flex flex-row items-center gap-2 justify-end flex-1 min-w-0">
          <label class="sr-only" for="sale-amount-input">Amount to sell</label>
          <input
            id="sale-amount-input"
            type="number"
            v-model.number="amount"
            min="0"
            step="any"
            class="w-1/2 max-w-xs bg-white rounded-lg p-2 border border-gray-200"
            placeholder="Amount"
            aria-label="Amount to sell"
          />
        </div>
      </div>
      <div class="grid grid-cols-4 gap-4 mt-4">
        <button
          type="button"
          @click="handleAmount(25)"
          class="bg-gray-200 rounded-lg p-2"
        >
          25%
        </button>
        <button
          type="button"
          @click="handleAmount(50)"
          class="bg-gray-200 rounded-lg p-2"
        >
          50%
        </button>
        <button
          type="button"
          @click="handleAmount(75)"
          class="bg-gray-200 rounded-lg p-2"
        >
          75%
        </button>
        <button
          type="button"
          @click="handleAmount(100)"
          class="bg-gray-200 rounded-lg p-2"
        >
          100%
        </button>
      </div>

      <div class="my-8 flex flex-row items-center justify-center">
        <v-divider></v-divider>
        <span class="mx-4 text-gray-500">to</span>
        <v-divider></v-divider>
      </div>

      <div class="flex flex-row justify-between items-center">
        <span>To:</span>
        <div
          v-if="saleQuote"
          class="text-right text-gray-800"
        >
          {{ saleQuote.to_amount }} NGN wallet
        </div>
        <div v-else class="text-right text-sm text-gray-500">
          Estimated amount after quote
        </div>
      </div>

      <p v-if="amountExceedsBalance" class="mt-2 text-sm error--text">
        Amount exceeds your available balance.
      </p>

      <p v-if="!saleIsEnabled" class="mt-2 text-sm error--text">
        Selling is currently disabled for {{ selectedAssetSymbol }}.
      </p>

      <div class="mt-8">
        <v-btn
          @click="getSaleQuote"
          :disabled="!canGetSaleQuote || loadingSaleQuote"
          :loading="loadingSaleQuote"
          block
          large
          color="primary"
        >
          Get Sale Quote
        </v-btn>
      </div>
    </div>

    <div
      v-else
      class="rounded-xl border border-gray-200 bg-white p-8 text-center"
    >
      <p class="text-gray-700 mb-4">
        We could not load your wallet for this asset. Try another coin from
        crypto, or fund your wallet first.
      </p>
      <div class="d-flex flex-wrap justify-center gap-2">
        <v-btn color="primary" outlined @click="goBack">Go back</v-btn>
        <v-btn color="primary" to="/user/products/crypto">Crypto</v-btn>
        <v-btn color="primary" text to="/user/wallet">Wallet</v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import cryptocoin from '../../../../api/users/cryptocoin'
import { formatCryptoAmount } from '../../../../utils/cryptoHistory'

export default {
  data() {
    return {
      showSaleQuoteModal: false,
      selectedAssetSymbol: '',
      currentAsset: null,
      coinId: '',
      walletAddressData: null,
      initialLoading: true,
      amount: 0,
      loadingSaleQuote: false,
      saleQuote: null,
      saleQuoteExpiresIn: 0,
      quoteCountdownTimer: null
    }
  },
  computed: {
    formattedWalletBalance() {
      if (!this.walletAddressData) return '0'
      return formatCryptoAmount(this.walletAddressData.balance)
    },
    numericAmount() {
      const n = Number(this.amount)
      return Number.isFinite(n) ? n : 0
    },
    numericBalance() {
      if (!this.walletAddressData) return 0
      const n = Number(this.walletAddressData.balance)
      return Number.isFinite(n) ? n : 0
    },
    amountExceedsBalance() {
      return this.numericAmount > this.numericBalance
    },
    saleIsEnabled() {
      return (
        !this.walletAddressData ||
        !this.walletAddressData.coin ||
        this.walletAddressData.coin.saleEnabled !== false
      )
    },
    canGetSaleQuote() {
      return (
        !!this.currentAsset &&
        this.numericAmount > 0 &&
        !this.amountExceedsBalance &&
        this.saleIsEnabled
      )
    },
    quoteIsExpired() {
      return (
        this.showSaleQuoteModal &&
        !!this.saleQuote &&
        this.saleQuoteExpiresIn <= 0
      )
    }
  },
  beforeDestroy() {
    this.clearQuoteCountdown()
  },
  async mounted() {
    this.coinId = this.$route.params.id
    try {
      await Promise.all([this.getCryptoAsset(), this.getCryptoAddress()])
    } finally {
      this.initialLoading = false
    }
  },
  methods: {
    alertUser(message, error = true) {
      this.$store.dispatch('alert', {
        message,
        status: true,
        error
      })
    },
    saleApiErrorMessage(res, fallback) {
      if (!res || !res.error) return fallback
      const em = res.errorMessage
      if (typeof em === 'string' && em.trim()) return em
      if (em && typeof em === 'object' && typeof em.message === 'string') {
        return em.message
      }
      return fallback
    },
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1)
      } else {
        this.$router.push('/user/products/crypto')
      }
    },
    clearQuoteCountdown() {
      if (this.quoteCountdownTimer != null) {
        clearInterval(this.quoteCountdownTimer)
        this.quoteCountdownTimer = null
      }
    },
    startQuoteCountdown(expiresAt) {
      this.clearQuoteCountdown()
      const end = new Date(expiresAt).getTime()
      if (Number.isNaN(end)) {
        this.saleQuoteExpiresIn = 0
        return
      }
      const tick = () => {
        const sec = Math.max(0, Math.floor((end - Date.now()) / 1000))
        this.saleQuoteExpiresIn = sec
        if (sec <= 0) {
          this.clearQuoteCountdown()
        }
      }
      tick()
      this.quoteCountdownTimer = setInterval(tick, 1000)
    },
    onSaleQuoteModalInput(value) {
      this.showSaleQuoteModal = value
      if (!value) {
        this.clearQuoteCountdown()
      }
    },
    async getCryptoAsset() {
      try {
        const res = await cryptocoin.cryptoCoin().getCryptosRate()
        const currentCoin = res.data.data.find(
          (asset) => asset.coinId === this.coinId
        )
        if (currentCoin) {
          this.currentAsset = {
            symbol: currentCoin.symbol,
            name: currentCoin.currency,
            logo: currentCoin.logo,
            coinId: currentCoin.coinId
          }
          this.selectedAssetSymbol = currentCoin.symbol
        } else {
          this.alertUser('This cryptocurrency is not available for sale.')
        }
      } catch (error) {
        this.alertUser(
          'Could not load cryptocurrency details. Please try again later.'
        )
      }
    },
    async getCryptoAddress() {
      const res = await cryptocoin.cryptoCoin().getCryptosWallets()
      if (res.error) {
        this.alertUser(res.message || 'Could not load wallets')
        return
      }
      const wallet = res.data.data.find((w) => w.coin._id === this.coinId)
      this.walletAddressData = wallet
    },
    handleAmount(percent) {
      if (!this.walletAddressData) return
      const bal = Number(this.walletAddressData.balance)
      if (!Number.isFinite(bal)) return
      this.amount = (bal * percent) / 100
    },
    async getSaleQuote() {
      this.loadingSaleQuote = true
      try {
        if (!this.currentAsset) {
          this.alertUser('Please select an asset')
          return
        }
        if (this.numericAmount <= 0) {
          this.alertUser('Please enter an amount greater than zero')
          return
        }
        if (this.amountExceedsBalance) {
          this.alertUser('Amount exceeds your available balance')
          return
        }
        if (!this.saleIsEnabled) {
          this.alertUser(
            `Selling is currently disabled for ${this.selectedAssetSymbol}`
          )
          return
        }

        const res = await cryptocoin.cryptoCoin().getSaleRate({
          amount: this.amount,
          coinId: this.coinId
        })

        if (res.error) {
          this.alertUser(
            this.saleApiErrorMessage(
              res,
              'An error occurred while getting the sale quote'
            )
          )
          return
        }

        const quote = res.data && res.data.data
        if (!quote) {
          this.alertUser('Invalid response from server. Please try again.')
          return
        }

        this.saleQuote = quote

        const fromAmt = Number(this.saleQuote.from_amount)
        const toAmt = Number(this.saleQuote.to_amount)
        const calculatedRate =
          Number.isFinite(fromAmt) && fromAmt !== 0 ? toAmt / fromAmt : 0
        this.saleQuote.rate = Number(calculatedRate).toFixed(2)

        this.startQuoteCountdown(this.saleQuote.expires_at)
        this.showSaleQuoteModal = true
      } catch (e) {
        const msg =
          (e &&
            e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          (e && e.message) ||
          'Could not get sale quote. Check your connection and try again.'
        this.alertUser(msg)
      } finally {
        this.loadingSaleQuote = false
      }
    },
    async refreshSaleQuote() {
      if (!this.saleQuote || !this.saleQuote.quoteId) return

      this.loadingSaleQuote = true
      try {
        const res = await cryptocoin.cryptoCoin().refreshSaleRate({
          amount: this.amount,
          coinId: this.coinId,
          quoteId: this.saleQuote.quoteId
        })

        if (res.error) {
          this.alertUser(
            this.saleApiErrorMessage(
              res,
              'An error occurred while refreshing the sale quote'
            )
          )
          return
        }

        const quote = res.data && res.data.data
        if (!quote) {
          this.alertUser('Invalid response from server. Please try again.')
          return
        }
        this.saleQuote = quote

        this.alertUser('Sale quote refreshed successfully', false)

        const fromAmt = Number(this.saleQuote.from_amount)
        const toAmt = Number(this.saleQuote.to_amount)
        const calculatedRate =
          Number.isFinite(fromAmt) && fromAmt !== 0 ? toAmt / fromAmt : 0
        this.saleQuote.rate = Number(calculatedRate).toFixed(2)

        this.startQuoteCountdown(this.saleQuote.expires_at)
      } catch (e) {
        const msg =
          (e &&
            e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          (e && e.message) ||
          'Could not refresh quote. Check your connection and try again.'
        this.alertUser(msg)
      } finally {
        this.loadingSaleQuote = false
      }
    },
    async completeSaleQuote() {
      if (this.quoteIsExpired) return

      this.loadingSaleQuote = true
      try {
        const res = await cryptocoin.cryptoCoin().completeSaleRate({
          amount: this.amount,
          coinId: this.coinId,
          quoteId: this.saleQuote.quoteId
        })

        if (res.error) {
          this.alertUser(
            this.saleApiErrorMessage(
              res,
              'An error occurred while completing the sale'
            )
          )
          return
        }

        const trade = res.data && res.data.data
        this.clearQuoteCountdown()
        this.showSaleQuoteModal = false

        // The backend marks the trade "processing" while it waits on the
        // provider to settle (funds are not credited instantly), so we must
        // not claim it's fully complete here — that was the root cause of
        // swaps/sales appearing to "succeed" with no balance change.
        if (trade && trade.status === 'processing') {
          this.alertUser(
            'Sale submitted and is being processed. Your NGN wallet will be credited shortly.',
            false
          )
        } else if (trade && trade.status === 'failed') {
          this.alertUser(
            trade.statusMessage || 'Sale failed. Please try again.'
          )
          return
        } else {
          this.alertUser('Sale completed successfully', false)
        }

        this.$router.push('/user/wallet')
      } catch (e) {
        const msg =
          (e &&
            e.response &&
            e.response.data &&
            (e.response.data.message || e.response.data.error)) ||
          (e && e.message) ||
          'Could not complete sale. Check your connection and try again.'
        this.alertUser(msg)
      } finally {
        this.loadingSaleQuote = false
      }
    }
  }
}
</script>

<style scoped>
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
